import { useLazyQuery } from '@apollo/client';
import {
  useGetLicenseInfo,
  useGetTenantPrivilegesLazy,
} from '@investigator/features/admin/queries';
import { useGetEDRConfigurationsPublicLazy } from '@investigator/features/entities/queries';
import { QUERY_ESCALATION_CONFIGURATIONS_PUBLIC } from '@investigator/features/integrations/queries';
import { Auth } from 'aws-amplify';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { safeParse } from 'secure-json-parse';
import {
  CONSTANTS,
  getIdTokenSafely,
  mapTenantType,
  ROUTES,
} from '../../constants';
import { AuthState, CognitoUserInterface } from '../../types/auth-types';
import {
  eventBus,
  getUserId,
  PrivilegesUpdatedEvent,
  setupCrossTabListenerForType,
} from '../shared';
import { AuthContext, UserPrivileges, UserRole } from './AuthContext';

/** Member shape used in ORG federation */
interface Member {
  tenant_id?: string | null;
  display_name?: string | null;
  [key: string]: unknown;
}

interface AuthProviderProps {
  children?: ReactNode;
}

const { FEDERATION_TYPE, TENANT_TYPE } = CONSTANTS;

/* ── Timers / timeouts ───────────────────────────────────────────────────── */
/** Timeout for a single privilege-load attempt */
const PRIVILEGE_LOAD_TIMEOUT_MS = 15000;
/** Polling interval when waiting for an in-flight tenant-privilege load to finish */
const PRIVILEGE_CHECK_INTERVAL_MS = 100;
/** Max polling attempts (keeps the total wait at ~timeout) */
const MAX_PRIVILEGE_CHECK_ATTEMPTS = 150;
/** Debounce used when processing license → initial loads (and ORG preloads) */
const LICENSE_PROCESS_DEBOUNCE_MS = 100;
/** Debounce used for ORG member privilege preloads; keeps bursty churn low */
const ORG_PRELOAD_DEBOUNCE_MS = 150;

/**
 * AuthProvider component that manages authentication state and user privileges
 * Provides authentication context to the entire application
 *
 * Centralizes:
 * - Auth state + Cognito user
 * - License info (normalized)
 * - Tenant privilege loading (current tenant + ORG member preloads)
 * - Cross-tab privilege updates
 * - Safe cleanup of timers
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authState, setAuthState] = useState<AuthState>();
  const [user, setUser] = useState<CognitoUserInterface>();
  const [userAlias, setUserAlias] = useState<string>();
  const [userRole, setUserRole] = useState<UserRole>();
  /** Map tenant_id → privilege record */
  const [userPrivileges, setUserPrivileges] = useState<
    Record<string, UserPrivileges>
  >({});
  /** Tenants for which we have attempted and finalized a privilege load */
  const [loadedTenants, setLoadedTenants] = useState<Set<string>>(new Set());
  const [isPrimeTenant, setIsPrimeTenant] = useState<boolean>(false);

  const lastUserIdRef = useRef<string | null>(null);
  /** Tracks in-flight privilege loads by tenant */
  const privilegeLoadingRef = useRef<Record<string, boolean>>({});
  /** Central registry of timeouts/intervals so we can cancel on unmount/user-change */
  const cleanupTimeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set());

  // Queries / hooks
  const [
    getUserLicense,
    { loading: userLicenseLoading, data: userLicenseData },
  ] = useGetLicenseInfo({ fetchPolicy: 'network-only' });

  const [getTenantPrivilegesLazy] = useGetTenantPrivilegesLazy({
    fetchPolicy: 'network-only',
  });

  const [getEDRConfigsLazy] = useGetEDRConfigurationsPublicLazy({
    fetchPolicy: 'network-only',
  });

  const [getServiceNowConfigsLazy] = useLazyQuery(
    QUERY_ESCALATION_CONFIGURATIONS_PUBLIC,
    { fetchPolicy: 'network-only' },
  );

  // Derived user context
  const userId = useMemo(() => getUserId(user), [user]);
  const userTenantId = user?.attributes?.['custom:tenant_id'];

  // Normalized license object
  const rawUserLicense = userLicenseData?.getLicenseInfo;
  const userLicense = useMemo(
    () =>
      rawUserLicense
        ? {
            ...rawUserLicense,
            tenant_type: mapTenantType(rawUserLicense.tenant_type ?? undefined),
          }
        : undefined,
    [rawUserLicense],
  );

  /**
   * Clear any active intervals/timeouts we registered.
   * Call this on unmount and whenever we hard-reset user state.
   */
  const clearAllTimeouts = useCallback(() => {
    cleanupTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    cleanupTimeoutsRef.current.clear();
  }, []);

  /**
   * Loads privileges for a specific tenant with timeout protection
   * Fetches tenant privileges, EDR configs, and ServiceNow configs in parallel
   *
   * Behavior:
   * - Coalesces concurrent loads per-tenant (waits on the first).
   * - Adds EDR + ServiceNow settings into the privilege bag for convenience.
   * - Times out after PRIVILEGE_LOAD_TIMEOUT_MS.
   * - Caches a "loaded" state in `loadedTenants` (successful or failed) to avoid thrash.
   */
  const loadPrivilegesForTenant = useCallback(
    async (tenantId: string): Promise<UserPrivileges> => {
      if (!tenantId) return {};
      // Check if already loaded
      if (loadedTenants.has(tenantId)) {
        return userPrivileges[tenantId] || {};
      }

      // Check if already loading
      if (privilegeLoadingRef.current[tenantId]) {
        // Wait for existing load to complete with timeout
        return new Promise((resolve) => {
          let attempts = 0;
          const checkInterval = setInterval(() => {
            attempts++;

            // Check if loading completed
            if (!privilegeLoadingRef.current[tenantId]) {
              clearInterval(checkInterval);
              resolve(userPrivileges[tenantId] || {});
            }
            // Check if we've hit the max attempts
            else if (attempts >= MAX_PRIVILEGE_CHECK_ATTEMPTS) {
              clearInterval(checkInterval);
              resolve(userPrivileges[tenantId] || {});
            }
          }, PRIVILEGE_CHECK_INTERVAL_MS);

          // Store timeout for cleanup
          cleanupTimeoutsRef.current.add(checkInterval);
        });
      }

      privilegeLoadingRef.current[tenantId] = true;

      try {
        // Create a timeout promise
        const timeoutPromise = new Promise<never>((_, reject) => {
          const timeout = setTimeout(
            () =>
              reject(
                new Error(`Privilege loading timed out for tenant ${tenantId}`),
              ),
            PRIVILEGE_LOAD_TIMEOUT_MS,
          );
          cleanupTimeoutsRef.current.add(timeout);
        });

        // Load all privileges in parallel with timeout
        const loadPromise = Promise.all([
          getTenantPrivilegesLazy({
            variables: { tenant_id: tenantId },
            fetchPolicy: 'network-only',
          }),
          getEDRConfigsLazy({
            variables: { tenant: tenantId },
            fetchPolicy: 'network-only',
          }),
          getServiceNowConfigsLazy({
            variables: { tenant: tenantId },
            fetchPolicy: 'network-only',
          }),
        ]);

        const [privilegesRes, edrRes, serviceNowRes] = (await Promise.race([
          loadPromise,
          timeoutPromise,
        ])) as Awaited<typeof loadPromise>;

        // Parse and combine privileges
        const privileges: UserPrivileges = safeParse(
          privilegesRes?.data?.getTenantPrivileges || '{}',
        );

        // Add EDR configurations
        edrRes?.data?.getEDRConfigurations?.edr_configurations?.forEach(
          (config: {
            provider_name: string;
            enabled: boolean;
            edr_action_enabled: boolean;
          }) => {
            privileges[config.provider_name] = config.enabled;
            privileges[`${config.provider_name}_action_enabled`] =
              config.edr_action_enabled;
          },
        );

        // Add ServiceNow configuration
        const serviceNowConfig =
          serviceNowRes?.data?.queryEscalationConfigurations
            ?.escalation_configurations?.[0];
        if (serviceNowConfig) {
          privileges['service-now'] = serviceNowConfig.enabled;
        }

        // Update state
        setUserPrivileges((prev) => ({ ...prev, [tenantId]: privileges }));
        setLoadedTenants((prev) => new Set(prev).add(tenantId));

        return privileges;
      } catch (error) {
        console.error(
          `[AuthProvider] Failed to load privileges for tenant ${tenantId}:`,
          error,
        );

        // Set empty privileges on failure to prevent repeated attempts
        setUserPrivileges((prev) => ({ ...prev, [tenantId]: {} }));
        setLoadedTenants((prev) => new Set(prev).add(tenantId));

        return {};
      } finally {
        privilegeLoadingRef.current[tenantId] = false;
      }
    },
    [
      loadedTenants,
      userPrivileges,
      getTenantPrivilegesLazy,
      getEDRConfigsLazy,
      getServiceNowConfigsLazy,
    ],
  );

  /**
   * Loads privileges for the *current* tenant (the user’s tenant).
   * Coalesces via `loadPrivilegesForTenant`.
   */
  const getCurrentTenantPrivileges = useCallback(async () => {
    const tenantId = user?.attributes?.['custom:tenant_id'];
    if (!tenantId || !userLicense) {
      return {};
    }
    if (loadedTenants.has(tenantId) || privilegeLoadingRef.current[tenantId]) {
      return loadPrivilegesForTenant(tenantId);
    }
    return loadPrivilegesForTenant(tenantId);
  }, [user, userLicense, loadPrivilegesForTenant, loadedTenants]);

  /* ── User change handling: clear state & fire event ────────────────────── */
  useEffect(() => {
    if (userId && userId !== lastUserIdRef.current) {
      const previousUserId = lastUserIdRef.current;

      if (previousUserId) {
        setUserPrivileges({});
        setLoadedTenants(new Set());
        privilegeLoadingRef.current = {};
        clearAllTimeouts();
      }

      lastUserIdRef.current = userId;

      // Emit user change event
      eventBus.emit('user-changed', {
        userId,
        previousUserId,
      });
    }
  }, [userId, clearAllTimeouts]);

  /* ── Logout handling: full reset ──────────────────────────────────────── */
  useEffect(() => {
    const handleLogout = () => {
      setAuthState(undefined);
      setUser(undefined);
      setUserAlias(undefined);
      setUserRole(undefined);
      setUserPrivileges({});
      setLoadedTenants(new Set());
      setIsPrimeTenant(false);

      // Clear refs
      lastUserIdRef.current = null;
      privilegeLoadingRef.current = {};
      clearAllTimeouts();
    };

    eventBus.on('user-logout', handleLogout);
    return () => {
      eventBus.off('user-logout', handleLogout);
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  /* ── Cross-tab privilege updates ──────────────────────────────────────── */
  useEffect(() => {
    const handlePrivilegesUpdate = (event: PrivilegesUpdatedEvent) => {
      setUserPrivileges((prevPrivileges) => ({
        ...prevPrivileges,
        [event.data.tenantId]: {
          ...prevPrivileges[event.data.tenantId],
          ...event.data.privileges,
        },
      }));

      // Emit local event for components that need to react
      eventBus.emit('privileges-updated', {
        tenantId: event.data.tenantId,
        privileges: event.data.privileges,
      });
    };

    const cleanup = setupCrossTabListenerForType(
      'privileges-updated',
      handlePrivilegesUpdate,
    );
    return cleanup;
  }, []);

  /* ── License fetch once authenticated ─────────────────────────────────── */
  useEffect(() => {
    async function setUserLicense() {
      try {
        if (user && authState === AuthState.SignedIn) {
          // Ensure we have a valid ID token before hitting AppSync
          const token = await getIdTokenSafely();
          if (!token) {
            // Attempt to recover session
            try {
              await Auth.currentSession();
            } catch (sessionError) {
              console.error(
                '[AuthProvider] Failed to recover session:',
                sessionError,
              );
              return;
            }
          }

          await getUserLicense();
        }
      } catch (error) {
        console.error('[AuthProvider] Error fetching user license:', error);
      }
    }
    setUserLicense();
  }, [user, authState, getUserLicense]);

  /**
   * Process license → load privileges / preloads (debounced).
   *
   * - Validates PRIME requirements (must have tenant_id).
   * - Validates license tenant matches authenticated user.
   * - Loads current-tenant privileges.
   * - In ORG federation, **debounces** preload of member-tenant privileges to smooth bursts.
   */
  useEffect(() => {
    // Skip if essential data is missing
    if (!userLicense || !user || authState !== AuthState.SignedIn) {
      return;
    }

    // Skip if license is still being determined
    if (!userLicense.tenant_type || !userLicense.tenant) {
      return;
    }

    // Use a debounce timer to prevent rapid re-runs
    const debounceTimer = setTimeout(() => {
      const isPrime = userLicense.tenant_type === TENANT_TYPE.PRIME;
      setIsPrimeTenant(isPrime);

      // Validate Prime tenant requirements
      if (isPrime) {
        const tenantId = userTenantId;
        if (!tenantId) {
          console.error(
            '[AuthProvider] Prime tenant missing tenant_id, signing out',
          );
          setAuthState?.(undefined);
          setUser?.(undefined);
          window.location.replace(ROUTES.login);
          return;
        }
      }

      // Validate license tenant matches user tenant
      if (userLicense.tenant !== userTenantId) {
        console.error('[AuthProvider] License tenant mismatch, signing out', {
          licenseTenant: userLicense.tenant,
          userTenant: userTenantId,
        });
        setUser?.(undefined);
        setAuthState?.(undefined);
        window.location.replace(ROUTES.login);
        return;
      }

      // Lazy-load privileges for the current tenant
      getCurrentTenantPrivileges().catch((error) => {
        console.error(
          '[AuthProvider] Failed to load initial privileges:',
          error,
        );
      });
    }, LICENSE_PROCESS_DEBOUNCE_MS);
    cleanupTimeoutsRef.current.add(debounceTimer);

    // Debounce ORG member preloads separately to avoid contention
    let orgPreloadTimer: NodeJS.Timeout | undefined;

    if (userLicense?.federation_type === FEDERATION_TYPE.ORG) {
      // members can contain null entries; guard accordingly
      const members = ((userLicense.members ?? []) as Member[]).filter(
        (m): m is Member => m != null,
      );

      orgPreloadTimer = setTimeout(() => {
        for (const m of members) {
          const tid = m?.tenant_id ?? undefined;
          // Skip if already loaded / currently loading
          if (
            !tid ||
            loadedTenants.has(tid) ||
            privilegeLoadingRef.current[tid]
          ) {
            continue;
          }
          // Fire-and-forget
          loadPrivilegesForTenant(tid);
        }
      }, ORG_PRELOAD_DEBOUNCE_MS);

      cleanupTimeoutsRef.current.add(orgPreloadTimer);
    }

    return () => {
      clearTimeout(debounceTimer);
      if (orgPreloadTimer) clearTimeout(orgPreloadTimer);
    };
  }, [
    user,
    userLicense,
    userTenantId,
    authState,
    getCurrentTenantPrivileges,
    loadedTenants,
    loadPrivilegesForTenant,
  ]);

  /* ── Component unmount cleanup ────────────────────────────────────────── */
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  /* ── Context value ────────────────────────────────────────────────────── */
  const value = useMemo(() => {
    const currentTenantId = user?.attributes?.['custom:tenant_id'];
    const currentPrivileges = currentTenantId
      ? userPrivileges[currentTenantId]
      : undefined;
    const isPrivilegesLoading = currentTenantId
      ? privilegeLoadingRef.current[currentTenantId] || false
      : false;

    const mappedType = userLicense?.tenant_type;

    return {
      authState,
      setAuthState,
      user,
      setUser,
      userAlias,
      setUserAlias,
      userRole,
      setUserRole,
      userPrivileges,
      setUserPrivileges,
      userPrivilegesLoading: isPrivilegesLoading,
      setUserPrivilegesLoading: (loading: React.SetStateAction<boolean>) => {
        if (currentTenantId) {
          const newValue =
            typeof loading === 'function'
              ? loading(privilegeLoadingRef.current[currentTenantId] || false)
              : loading;
          privilegeLoadingRef.current[currentTenantId] = newValue;
        }
      },
      userLicense,
      userLicenseLoading,
      isOrgTenant: userLicense?.federation_type === FEDERATION_TYPE.ORG,
      isPOVTenant: mappedType === TENANT_TYPE.POV,
      isPrimeTenant,
      setIsPrimeTenant,
      loadPrivilegesForTenant,
      getCurrentTenantPrivileges,
      currentTenantPrivileges: currentPrivileges,
    };
  }, [
    authState,
    user,
    userAlias,
    userRole,
    userPrivileges,
    userLicense,
    userLicenseLoading,
    isPrimeTenant,
    loadPrivilegesForTenant,
    getCurrentTenantPrivileges,
  ]);

  return (
    <AuthContext.Provider value={{ ...value }}>{children}</AuthContext.Provider>
  );
};
